import * as EXIF from 'exif-js'

export const BASE_64_JPEG_START = 'data:image/jpeg;base64,'
export const BASE_64_REGEX = /^data:image\/(jpeg|png|jpg)\;base64\,.*/

/**
 * Returns a Promise resolving with the EXIF metadata for Orientation as a code
 * If such a data is not available, returns default code 1
 * See EXIF documentation to interpret the code 
 * (or see : https://www.impulseadventure.com/photo/images/orient_flag.gif)
 * @param image
 */
export function getImageOrientation(image): Promise<number> {
    if (image) {
        return new Promise((resolve, reject) => {
            EXIF.getData(image, function (this: HTMLImageElement) {
                try {
                    const orientation = EXIF.getTag(this, "Orientation")
                    if (orientation) {
                        resolve(orientation)
                    } else {
                        // in the case where no exif tag is present
                        resolve(1)
                    }
                } catch (error) {
                    // in the case where no exif tag is present
                    resolve(1)
                }
            })
            resolve(1)
        })
    }
    return new Promise((resolve, reject) => {
        reject("Error : No image provided")
    })
}

/**
 * 
 * @param base64 
 * @returns 
 * @throws
 */
export function convertBase64ToUint8Array(base64: string): Uint8Array {
    try{
        const byteString = atob(base64.split(',')[1])
        const arrayBuffer = new ArrayBuffer(byteString.length)
        const intArray = new Uint8Array(arrayBuffer)       
                                
        for (let i = 0; i < byteString.length; i++) {
            intArray[i] = byteString.charCodeAt(i)
        }
        return intArray
    } catch(err) {
        // logger MediaTransferHelper.convertBase64ToUint8Array: unvalid base64 string
        throw err
    }
}

export function convertBase64ToArrayBuffer(base64: string): ArrayBuffer {
    const bytes = convertBase64ToUint8Array(base64)
    return bytes.buffer
}

export function arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    let bytes = new Uint8Array( buffer );
    let len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode( bytes[ i ] );
    }
    let res: string = btoa( binary )
    if(!res.match(BASE_64_REGEX)) {
        return BASE_64_JPEG_START + res
    }
    return res
}

/**
 * 
 * @param base64Media 
 * @returns the orientation and the mediaData stripped of EXIF orientation (ArrayBuffer) | orientation = -1 and mediaData as string if failure occured
 */
export function extractAndRemoveExifOrientationFromMedia(base64Media: string): { orientation: number, mediaWithoutOrientation: ArrayBuffer | string } {
    const mediaArrayBuffer = convertBase64ToArrayBuffer(base64Media)
    const mediaDataView = new DataView(mediaArrayBuffer)

    let offsetExifOrientation: {start: number, end: number, isLittleEndian: boolean} | null = null
    try{
        offsetExifOrientation = getExifOrientationOffset(mediaDataView)
    } catch(err) {
        // we failed to retrieve orientation info, we use data image without handling orientation
        return {
            orientation: -1,
            mediaWithoutOrientation: base64Media,
        }
    }

    if(offsetExifOrientation !== null) {
        const orientation = mediaDataView.getUint16(
            offsetExifOrientation.end, 
            offsetExifOrientation.isLittleEndian
        )

        // set orientation to none in exif data
        if(orientation !== 1) {
            mediaDataView.setUint16(
                offsetExifOrientation.end,
                1,
                offsetExifOrientation.isLittleEndian
            )
        }

        return {
            orientation,
            mediaWithoutOrientation: mediaDataView.buffer
        }
    }

    return {
        orientation: -1,
        mediaWithoutOrientation: base64Media,
    }
}

/**
 * 
 * @param imageData 
 * @returns 
 * @throws
 */
function getExifOrientationOffset(imageData: DataView): { start: number, end: number, isLittleEndian: boolean} {
    if (imageData.getUint16(0, false) !== 0xFFD8) {
        throw new Error()
    }

    const length = imageData.byteLength
    let offset = 2

    while (offset < length) {
        if ((imageData.getUint16(offset + 2, false) <= 8)) {
            throw new Error()
        }
        const marker = imageData.getUint16(offset, false)
        offset += 2

        if (marker === 0xFFE1) {
           if (imageData.getUint32(offset += 2, false) !== 0x45786966) {
                throw new Error()
           }

           const little = imageData.getUint16(offset += 6, false) === 0x4949
           offset += imageData.getUint32(offset + 4, little)
           const tags = imageData.getUint16(offset, little)
           offset += 2
           for (let i = 0; i < tags; i++) {
              if (imageData.getUint16(offset + (i * 12), little) === 0x0112) {
                return { 
                    start: offset + (i * 12), 
                    end: offset + (i * 12) + 8,
                    isLittleEndian: little
                }
              }
           }
        } else if ((marker && 0xFF00) !== 0xFF00) {
           break
        } else {
           offset += imageData.getUint16(offset, false)
        }
    }
    throw new Error()
}

/**
 * 
 * @param image 
 * @param orientation 
 * @returns 
 * @throws
 */
export function rotateImage(image: HTMLImageElement, orientation: number): string {
    const canvas = document.createElement('canvas')

    const ctx = canvas.getContext('2d')
    if(ctx) {
        const width = image.width
        canvas.width = width

        if (orientation) {
            switch (orientation) {
                case 3:
                    let factor = canvas.width / image.width
                    canvas.height = image.height * factor
                    ctx.scale(factor, factor)
                    ctx.translate(image.width, image.height)
                    ctx.rotate(180 * Math.PI / 180)
                    ctx.drawImage(image, 0, 0)
                    break
                case 6:
                    factor = width / image.height
                    canvas.height = image.width * factor
                    ctx.scale(factor, factor)
                    ctx.translate(image.height, 0)
                    ctx.rotate(90 * Math.PI / 180)
                    ctx.drawImage(image, 0, 0)
                    break
                case 8:
                    factor = width / image.height
                    canvas.height = image.width * factor
                    ctx.scale(factor, factor)
                    ctx.translate(0, image.width)
                    ctx.rotate(-90 * Math.PI / 180)
                    ctx.drawImage(image, 0, 0)
                    break
                default:
                    factor = width / image.width
                    canvas.height = image.height * factor
                    ctx.scale(factor, factor)
                    ctx.drawImage(image, 0, 0)
                    break
            }
        } else {
            const factor = canvas.width / image.width
            canvas.height = image.height * factor
            ctx.scale(factor, factor)
            ctx.drawImage(image, 0, 0)
        }
        return canvas.toDataURL()
    }

    throw new Error()
}